/*
 * Copyright (c) 2019 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import { Subscription } from 'rxjs';

import {
  Directive,
  ElementRef,
  AfterViewInit,
  OnDestroy,
  ChangeDetectorRef,
  Renderer2,
  NgZone,
  Input,
  ViewContainerRef,
  ComponentFactoryResolver,
  ComponentRef,
  Type
} from '@angular/core';

import { ChangedEvent, TextService, MessageService } from '@typescript-kit/core';
import { TextInputModel, ChangeModes, ComponentModel, TextModel } from '@typescript-kit/view';

import { BaseDirective } from './base.directive';
import { TextComponent } from '../component/text/text.component';
import { ComponentProvider } from '../provider/component.provider';

@Directive({
  selector: '[kitTextInput]'
})
export class TextInputDirective extends BaseDirective<TextInputModel> implements AfterViewInit, OnDestroy {
  private unlistenInput: () => any;
  private inputTimeout: any;
  private autoselectTimeout: any;

  private suffixComponentRef: ComponentRef<BaseDirective<ComponentModel>>;
  private alignmentClass: string;

  constructor(
    private viewContainerRef: ViewContainerRef,
    private componentFactoryResolver: ComponentFactoryResolver,
    changeDetector: ChangeDetectorRef,
    elementRef: ElementRef,
    renderer: Renderer2,
    ngZone: NgZone,
    textService: TextService,
    protected messageService: MessageService
  ) {
    super(changeDetector, elementRef, renderer, ngZone, textService);
  }

  get hostClass(): string {
    return 'kit-text-input';
  }

  @Input()
  set kitTextInput(model: TextInputModel) {
    this.model = model;
  }

  get kitTextInput(): TextInputModel {
    return this.model;
  }

  get inputElement(): HTMLInputElement {
    return this.elementRef.nativeElement;
  }

  ngAfterViewInit(): void {
    // Update model after browser auto fill (not working with chrome)
    if (this.model && this.model.changeMode !== ChangeModes.IMMEDIATE) {
      setTimeout(() => {
        if (this.inputElement.value !== this.model.formattedValue) {
          this.model.formattedValue = this.inputElement.value;
        }
      }, 100);
    }
  }

  ngOnDestroy(): void {
    if (this.inputTimeout) {
      clearTimeout(this.inputTimeout);
    }
    if (this.autoselectTimeout) {
      clearTimeout(this.autoselectTimeout);
    }
    this.deregisterInputHandler();
    super.ngOnDestroy();
  }

  select() {
    this.inputElement.select();
  }

  protected onModelChanged(model: TextInputModel) {
    super.onModelChanged(model);
    this.refreshType();
    this.inputElement.disabled = model.isDisabled;
    this.refreshElementValue();
    this.refreshPlaceholder();
    this.refreshInputHandler();
    this.refreshAutocompleteToken();
    this.refreshSuffix();
    this.refreshAlignmentClass();
    this.refreshValidity();
    this.refreshErrorClass();
  }

  protected onModelPropertyChanged(event: ChangedEvent): void {
    super.onModelPropertyChanged(event);
    if (event.changes['isObscured']) {
      this.refreshType();
    }
    if (event.changes['isDisabled']) {
      this.inputElement.disabled = this.model.isDisabled;
    }
    if (event.changes['formattedValue']) {
      this.refreshElementValue();
    }
    if (event.changes['suffix']) {
      this.refreshSuffix();
    }
    if (event.changes['alignment']) {
      this.refreshAlignmentClass();
    }
    if (event.changes['isValid']) {
      this.refreshValidity();
      this.refreshErrorClass();
    }
    if (event.changes['placeholder']) {
      this.refreshPlaceholder();
    }
    if (event.changes['changeMode']) {
      this.refreshInputHandler();
    }
    if (event.changes['autocompleteToken']) {
      this.refreshAutocompleteToken();
    }
  }

  protected refreshModelValue() {
    this.model.formattedValue = this.inputElement.value;
    this.refreshElementValue(); // required if formatted value was not changed, but input element value was changed
  }

  protected refreshElementValue() {
    if (this.inputElement.value === this.model.formattedValue) {
      return; // do not set input value propery, if not changed (moves cursor in Safari to end)
    }
    this.inputElement.value = this.model.formattedValue;
  }

  protected autoselect(delay: number = 30) {
    if (!this.model.autoselect) {
      return;
    }
    if (this.autoselectTimeout !== null) {
      clearTimeout(this.autoselectTimeout);
    }
    this.autoselectTimeout = setTimeout(() => {
      this.autoselectTimeout = null;
      if (this.model.isFocused) {
        this.select();
      }
    }, delay);
  }

  protected onFocus(event: FocusEvent) {
    super.onFocus(event);
    this.autoselect();
  }

  protected onBlur(event: Event) {
    super.onBlur(event);
    if (this.model.formattedValue !== this.inputElement.value) {
      this.refreshModelValue();
    }
  }

  protected onEnter(event: KeyboardEvent) {
    super.onEnter(event);
    this.refreshModelValue();
    this.autoselect();
  }

  protected onEscape(event: KeyboardEvent) {
    super.onEscape(event);
    if (this.inputElement.value !== this.model.formattedValue) {
      this.inputElement.value = this.model.formattedValue;
    }
    this.autoselect();
  }

  protected onInput() {
    if (this.model.changeMode === ChangeModes.IMMEDIATE) {
      this.refreshModelValue();
      return;
    }
    if (this.inputTimeout) {
      clearTimeout(this.inputTimeout);
    }
    this.inputTimeout = setTimeout(() => {
      this.inputTimeout = null;
      this.refreshModelValue();
    }, this.model.changeDelay);
  }

  protected refreshType() {
    const typeAttribute = this.inputElement.getAttribute('type');
    if (!this.model.isObscured) {
      if (!typeAttribute || typeAttribute === 'password') {
        this.renderer.setAttribute(this.inputElement, 'type', 'text');
      }
    } else {
      if (!typeAttribute || typeAttribute !== 'password') {
        this.renderer.setAttribute(this.inputElement, 'type', 'password');
      }
    }
  }

  protected refreshPlaceholder() {
    let placeholder: string = '';
    if (!this.model.placeholder) {
      placeholder = '';
    } else if (typeof this.model.placeholder === 'string') {
      placeholder = this.textService.getText(this.model.placeholder);
    } else {
      placeholder = this.textService.getText(this.model.scope ? `${this.model.scope}/${this.model.key}` : this.model.key);
    }
    this.inputElement.placeholder = placeholder;
  }

  protected refreshAutocompleteToken() {
    if (this.model && this.model.autocompleteToken) {
      this.renderer.setAttribute(this.inputElement, 'autocomplete', this.model.autocompleteToken);
    } else {
      this.renderer.removeAttribute(this.inputElement, 'autocomplete');
    }
  }

  protected refreshSuffix() {
    if (this.suffixComponentRef) {
      if (this.model.isHidden || this.model.suffix === undefined || this.model.suffix === null) {
        this.viewContainerRef.remove(0);
        this.suffixComponentRef = null;
      } else if (typeof this.model.suffix !== 'string') {
        this.viewContainerRef.remove(0);
        this.suffixComponentRef = this.createComponent(this.model.suffix);
      } else if (this.suffixComponentRef.instance instanceof TextComponent) {
        this.suffixComponentRef.instance.model.text = this.model.suffix;
      } else {
        this.viewContainerRef.remove(0);
        this.suffixComponentRef = this.createComponent(new TextModel({
          tags: ['kit-value-suffix'],
          text: this.model.suffix
        }));
      }
    } else {
      if (this.model.isHidden || this.model.suffix === undefined || this.model.suffix === null) {
        return;
      }
      if (typeof this.model.suffix === 'string') {
        this.suffixComponentRef = this.createComponent(new TextModel({
          tags: ['kit-value-suffix'],
          text: this.model.suffix
        }));
      } else if (this.model.suffix) {
        this.suffixComponentRef = this.createComponent(this.model.suffix);
      }
    }
  }

  protected createComponent(model: ComponentModel): ComponentRef<BaseDirective<ComponentModel>> {
    const componentType: Type<any> = ComponentProvider.resolveComponentType(model.type);
    if (!componentType) {
      // TODO
      return;
    }
    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(componentType);
    const componentRef = this.viewContainerRef.createComponent(componentFactory);
    componentRef.instance.model = model;
    return componentRef;
  }

  protected refreshAlignmentClass() {
    if (this.alignmentClass) {
      this.renderer.removeClass(this.inputElement, this.alignmentClass);
      this.alignmentClass = null;
    }
    if (this.model && this.model.alignment) {
      this.alignmentClass = `kit-alignment-${this.model.alignment}`;
      this.renderer.addClass(this.inputElement, this.alignmentClass);
    }
  }

  protected refreshValidity() {
    const error = this.model.formatError || this.model.validationError;
    this.inputElement.setCustomValidity(error ? this.messageService.getMessage(error) : '');
  }

  protected refreshErrorClass() {
    if (this.model.isValid === false) {
      this.renderer.addClass(this.inputElement, 'app-has-error');
    } else {
      this.renderer.removeClass(this.inputElement, 'app-has-error');
    }
  }

  protected refreshInputHandler() {
    if (this.model.changeMode === ChangeModes.TRIGGERED) {
      this.deregisterInputHandler();
    } else {
      this.registerInputHandler();
    }
  }

  protected registerInputHandler() {
    if (this.unlistenInput) {
      return;
    }
    this.unlistenInput = this.renderer.listen(this.inputElement, 'input', (event: Event) => {
      this.ngZone.run(() => this.onInput());
    });
  }

  protected deregisterInputHandler() {
    if (!this.unlistenInput) {
      return;
    }
    this.unlistenInput();
    this.unlistenInput = null;
  }

}
